import {
  Component,
  Host,
  h,
  Element,
  Prop,
  State,
  Watch
} from "@stencil/core";

import { setPropsIfNull, hasSlot } from "../../utils/utils";

const CLASS_HAS_STICKED = "has-sticked";
const CLASS_IS_STICKY_LEFT = "is-sticky-left";
const CLASS_IS_STICKY_RIGHT = "is-sticky-right";

/**
 * @slot - Slot for table element
 * @slot heading - Slot for `stzh-heading`
 * @slot search - Slot for search `stzh-input` element
 * @slot action-button - Slot for action `stzh-button` element
 * @slot list - Slot for list element showing on mobile
 * @slot pagination - Slot for action `stzh-pagination` element
 * @slot list-sort-by-dropdown - Slot for a dropdown to sort by a specific criterion
 * @slot list-sort-direction-dropdown - Slot for a dropdown to choose the sort direction
 */
@Component({
  tag: "stzh-table",
  styleUrl: "stzh-table.scss"
})
export class StzhTable {
  /** Columns that should be sticky on the left side (column indexes separated by comma) */
  @Prop() fixedLeft: number = null;

  /** Columns that should be sticky on the right side (column indexes separated by comma) */
  @Prop() fixedRight: number = null;

  /**
   * Whether to disable min-width of the table.
   *
   * If `false`, the default `min-width` is `700px`
   * If `true`, the `min-width` is `none`, so the table will try to squeeze down content as much as possible.
   *
   * You can also overwrite the min-width via CSS with the custom property `--min-width`.
   */
  @Prop({ reflect: true }) disableMinWidth: boolean = false;

  @State() hasScrollLeft: boolean = false;
  @State() hasScrollRight: boolean = false;
  @State() isScrollable: boolean = false;
  @State() cellsStickyLeft: number[] = [];
  @State() cellsStickyRight: number[] = [];

  @Element() element: HTMLStzhTableElement;

  @Watch("fixedLeft")
  @Watch("fixedRight")
  fixedCellWatcher() {
    this.updateCellClasses();
    this.updateScrollStates();
  }

  private tableWrapper: HTMLDivElement;
  private observer: MutationObserver;
  private debounceScroll: number;

  private debounceResize: number;
  private resizeObserver: ResizeObserver;

  private updateCellClasses = () => {
    if (!this.tableWrapper) {
      return;
    }

    const rows: HTMLTableRowElement[] = Array.from(this.tableWrapper.querySelectorAll("tr"));
    rows.forEach((row: HTMLTableRowElement) => {
      const cells = Array.from(row.children);
      cells.forEach((cell: HTMLTableCellElement, index) => {
        cell.classList.remove(CLASS_IS_STICKY_LEFT);
        cell.classList.remove(CLASS_IS_STICKY_RIGHT);

        if (index === this.fixedLeft) {
          cell.classList.add(CLASS_IS_STICKY_LEFT);
        }

        if (index === this.fixedRight) {
          cell.classList.add(CLASS_IS_STICKY_RIGHT);
        }
      });
    });
  }

  private updateScrollStates = () => {
    if (!this.tableWrapper) {
      return;
    }

    const wrapperScrollLeft = this.tableWrapper.scrollLeft;
    const wrapperScrollWidth = this.tableWrapper.scrollWidth;
    const wrapperClientWidth = this.tableWrapper.clientWidth;

    this.isScrollable = wrapperScrollWidth > wrapperClientWidth;
    this.hasScrollLeft = wrapperScrollLeft > 0;
    this.hasScrollRight = wrapperScrollLeft < wrapperScrollWidth - wrapperClientWidth;

    const rows: HTMLTableRowElement[] = Array.from(this.tableWrapper.querySelectorAll("tr"));
    const firstRow = rows[0];

    if (!firstRow) {
      return;
    }

    this.cellsStickyLeft = [];
    this.cellsStickyRight = [];
    const cells: Element[] = Array.from(firstRow.children);

    cells.forEach((cell: HTMLTableCellElement, index: number) => {
      const offsetLeft = cell.offsetLeft;
      const offsetRight = offsetLeft + cell.offsetWidth;

      if (
        this.hasScrollLeft
        && index === this.fixedLeft
        && Math.ceil(wrapperScrollLeft) >= offsetLeft
      ) {
        this.cellsStickyLeft.push(index);
      }

      if (
        this.hasScrollRight
        && index === this.fixedRight
        && Math.ceil(wrapperScrollLeft) + wrapperClientWidth <= offsetRight
      ) {
        this.cellsStickyRight.push(index);
      }
    });

    rows.forEach((row: HTMLTableRowElement) => {
      const cells = Array.from(row.children);
      cells.forEach((cell: HTMLTableCellElement, index: number) => {
        if (
          this.cellsStickyLeft.indexOf(index) > -1
          || this.cellsStickyRight.indexOf(index) > -1
        ) {
          cell.classList.add(CLASS_HAS_STICKED);
        } else {
          cell.classList.remove(CLASS_HAS_STICKED);
        }
      });
    });
  }

  private init = () => {
    const searchInput = this.element.querySelector(
      'stzh-input[slot="search"], [slot="search"] stzh-input'
    );

    const listSortDropdowns = this.element.querySelectorAll(
      'stzh-dropdown[slot="list-sort-by-dropdown"], [slot="list-sort-by-dropdown"] stzh-dropdown, stzh-dropdown[slot="list-sort-direction-dropdown"], [slot="list-sort-direction-dropdown"] stzh-dropdown'
    );

    if (searchInput) {
      setPropsIfNull(searchInput, {
        type: "search",
        size: "small",
        hideOptional: true
      } as HTMLStzhInputElement);
    }

    listSortDropdowns.forEach(sortDropdown => {
      setPropsIfNull(sortDropdown, {
        inline: true,
        noSearch: true,
        variant: "plain",
        size: "small"
      } as HTMLStzhDropdownElement)
    });

    this.updateCellClasses();
    this.updateScrollStates();
  }

  handleResize = () => {
    if (this.debounceResize) {
      window.cancelAnimationFrame(this.debounceResize);
    }

    this.debounceResize = requestAnimationFrame(this.updateScrollStates);
  }

  private handleScroll = () => {
    if (this.debounceScroll) {
      window.cancelAnimationFrame(this.debounceScroll);
    }

    this.debounceScroll = requestAnimationFrame(this.updateScrollStates);
  }

  connectedCallback() {
    this.init();

    this.observer = new MutationObserver(this.init);
    this.observer.observe(this.element, {
      childList: true,
      subtree: true
    });

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.element);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  render() {
    const headingSlotUsed = hasSlot(this.element, "heading");
    const actionButtonSlotUsed = hasSlot(this.element, "action-button");
    const searchSlotUsed = hasSlot(this.element, "search");
    const hasList = hasSlot(this.element, "list");

    const classes = {
      "stzh-table": true,
      "is-scrollable": this.isScrollable,
      "has-scroll-left": this.hasScrollLeft,
      "has-scroll-right": this.hasScrollRight,
      "has-scroll-left-shadow": this.hasScrollLeft && this.cellsStickyLeft.length === 0,
      "has-scroll-right-shadow": this.hasScrollRight && this.cellsStickyRight.length === 0,
      "has-list": hasList,
      "has-heading": headingSlotUsed,
      "has-action-or-search": actionButtonSlotUsed || searchSlotUsed,
      "has-header": headingSlotUsed || actionButtonSlotUsed || searchSlotUsed
    };

    return (
      <Host>
        <div class={classes}>
          <div class="stzh-table__header">
            <div class="stzh-table__heading">
              <slot name="heading"></slot>
            </div>
            <div class="stzh-table__sub-header">
              <div class="stzh-table__search">
                <slot name="search"></slot>
              </div>
              <div class="stzh-table__action-button">
                <slot name="action-button"></slot>
              </div>
            </div>
          </div>
          <div class="stzh-table__outer-wrapper">
            <div
              ref={(el) => (this.tableWrapper = el as HTMLDivElement)}
              class="stzh-table__wrapper"
              onScroll={this.handleScroll}
            >
              <slot></slot>
            </div>
          </div>
          <div class="stzh-table__list-wrapper">
            <div class="stzh-table__list-sort-wrapper">
              <slot name="list-sort-by-dropdown"></slot>
              <slot name="list-sort-direction-dropdown"></slot>
            </div>
            <slot name="list"></slot>
          </div>
          <div class="stzh-table__pagination-wrapper">
            <slot name="pagination"></slot>
          </div>
        </div>
      </Host>
    );
  }
}
